import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { formatNumber } from "@/lib/utils-format";
import {
  Users,
  Calendar,
  ChartBar,
  PaperPlaneTilt,
  TrendUp,
  Books,
  ShieldWarning,
  Funnel,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

function StatCard({ icon: Icon, label, value, hint, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-5 hover:border-zinc-700 transition-colors"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          {label}
        </div>
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center"
          style={{ background: `${accent}15`, color: accent }}
        >
          <Icon size={14} weight="bold" />
        </div>
      </div>
      <div className="text-3xl font-mono font-semibold tracking-tight">
        {formatNumber(value)}
      </div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/stats/overview")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return <div className="text-zinc-500 text-sm">Загружаем сводку…</div>;
  if (!data) return null;

  const total = data.funnel?.started || 1;
  const fStarted = data.funnel?.started || 0;
  const fReg = data.funnel?.registered || 0;
  const fEmail = data.funnel?.with_email || 0;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Сводка"
        subtitle="Ключевые метрики по боту и подписчикам"
      />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        <StatCard
          icon={Users}
          label="Подписчиков всего"
          value={data.total_users}
          accent="#3b82f6"
          testid="stat-total-users"
        />
        <StatCard
          icon={Calendar}
          label="Активных ивентов"
          value={data.active_events}
          hint={`из ${data.total_events}`}
          accent="#2AABEE"
          testid="stat-active-events"
        />
        <StatCard
          icon={ChartBar}
          label="Регистраций (всего)"
          value={data.regs_total}
          hint={`+${data.regs_week} за неделю`}
          accent="#10b981"
          testid="stat-regs-total"
        />
        <StatCard
          icon={TrendUp}
          label="Регистраций сегодня"
          value={data.regs_today}
          accent="#a78bfa"
          testid="stat-regs-today"
        />
        <StatCard
          icon={PaperPlaneTilt}
          label="Рассылок"
          value={data.broadcasts_sent}
          hint={`${data.broadcasts_pending} в очереди`}
          accent="#ec4899"
          testid="stat-broadcasts"
        />
        <StatCard
          icon={Books}
          label="Программ в каталоге"
          value={data.programs_count}
          accent="#10b981"
          testid="stat-programs"
        />
        <StatCard
          icon={ShieldWarning}
          label="Заблокировали бот"
          value={data.blocked_users}
          accent="#ef4444"
          testid="stat-blocked"
        />
        <StatCard
          icon={Funnel}
          label="Незавершённых"
          value={data.incomplete}
          accent="#3b82f6"
          testid="stat-incomplete"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart */}
        <div className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                Регистрации
              </div>
              <div className="font-semibold mt-0.5">Последние 14 дней</div>
            </div>
          </div>
          {data.registrations_series.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-zinc-600 text-sm">
              Пока нет данных за этот период
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.registrations_series}>
                  <defs>
                    <linearGradient id="amber" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="day"
                    tick={{ fill: "#71717a", fontSize: 11 }}
                    axisLine={{ stroke: "#27272a" }}
                  />
                  <YAxis
                    tick={{ fill: "#71717a", fontSize: 11 }}
                    axisLine={{ stroke: "#27272a" }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#18181b",
                      border: "1px solid #27272a",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: "#a1a1aa" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#amber)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Funnel */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            Воронка
          </div>
          <div className="font-semibold mt-0.5 mb-5">Конверсия пользователей</div>
          <div className="space-y-4">
            {[
              { label: "Начали регистрацию", val: fStarted, color: "#3b82f6" },
              { label: "Завершили регистрацию", val: fReg, color: "#2AABEE" },
              { label: "Оставили email", val: fEmail, color: "#10b981" },
            ].map((f) => (
              <div key={f.label}>
                <div className="flex justify-between text-xs text-zinc-400 mb-1">
                  <span>{f.label}</span>
                  <span className="font-mono">
                    {formatNumber(f.val)} ·{" "}
                    {total ? Math.round((f.val / total) * 100) : 0}%
                  </span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${total ? (f.val / total) * 100 : 0}%`,
                      background: f.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {data.utm_top?.length > 0 && (
            <div className="mt-6 pt-5 border-t border-zinc-800">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3">
                Топ источников
              </div>
              <div className="space-y-2">
                {data.utm_top.map((u) => (
                  <div
                    key={u.source}
                    className="flex justify-between items-center text-xs"
                  >
                    <span className="text-zinc-300 font-mono">{u.source}</span>
                    <span className="text-zinc-500 font-mono">
                      {formatNumber(u.count)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
